import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export type UserRole = "admin" | "hr" | "interviewer" | "candidate";

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar: string;
  department?: string;
  profileCompleted?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  switchRole: (role: UserRole) => void;
  completeProfile: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const mockUsers: Record<string, User> = {
  "admin@hireflow.com": {
    id: "u1",
    name: "Admirra John",
    email: "admin@hireflow.com",
    role: "admin",
    avatar: "AJ",
    department: "HR",
    profileCompleted: true,
  },
  "hr@hireflow.com": {
    id: "u2",
    name: "Priya Kapoor",
    email: "hr@hireflow.com",
    role: "hr",
    avatar: "PK",
    department: "HR",
    profileCompleted: true,
  },
  "interviewer@hireflow.com": {
    id: "u3",
    name: "Rahul Mehta",
    email: "interviewer@hireflow.com",
    role: "interviewer",
    avatar: "RM",
    department: "Engineering",
    profileCompleted: true,
  },
  "candidate@hireflow.com": {
    id: "u4",
    name: "Sneha Patel",
    email: "candidate@hireflow.com",
    role: "candidate",
    avatar: "SP",
    profileCompleted: true,
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const login = useCallback(async (email: string, _password: string) => {
    const found = mockUsers[email.toLowerCase()];
    if (!found) {
      throw new Error("Invalid credentials. Try admin@hireflow.com");
    }
    setUser(found);
  }, []);

  const signup = useCallback(
    async (name: string, email: string, _password: string) => {
      if (mockUsers[email.toLowerCase()]) {
        throw new Error("An account with this email already exists");
      }
      const initials = name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
      const newUser: User = {
        id: `u${Date.now()}`,
        name,
        email: email.toLowerCase(),
        role: "candidate",
        avatar: initials,
        profileCompleted: false,
      };
      mockUsers[email.toLowerCase()] = newUser;
      setUser(newUser);
    },
    []
  );

  const logout = useCallback(() => setUser(null), []);

  const switchRole = useCallback((role: UserRole) => {
    setUser(prev => (prev ? { ...prev, role } : null));
  }, []);

  const completeProfile = useCallback(() => {
    setUser(prev => (prev ? { ...prev, profileCompleted: true } : null));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        switchRole,
        completeProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
